import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import StaffLayout from "@/components/StaffLayout";
import { usePump } from "@/context/PumpContext";
import { useProducts, emptyReadings } from "@/lib/products";
import { useMemo } from "react";
import ReadingsGrid from "@/components/ReadingsGrid";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Play, ArrowLeft } from "lucide-react";
import { toast } from "sonner";


export default function StartShift() {
  const nav = useNavigate();
  const { pump } = usePump();
  const products = useProducts();
  const EMPTY = useMemo(() => emptyReadings(products), [products]);
  const mpdCount = pump?.mpd_count || 2;
  const OPTIONS = [...Array.from({ length: mpdCount }, (_, i) => `MPD ${i + 1}`), ...(mpdCount >= 2 ? ["Both MPDs"] : [])];
  const [mpd, setMpd] = useState("MPD 1");
  const [readings, setReadings] = useState(EMPTY);
  const [readings2, setReadings2] = useState(EMPTY);
  const [openingCash, setOpeningCash] = useState("");
  const [busy, setBusy] = useState(false);
  const isDual = mpd === "Both MPDs";

  const submit = async (e) => {
    e.preventDefault();
    const allValid = (r) =>
      Object.keys(EMPTY).every((k) => r[k] !== "" && r[k] !== null && !isNaN(Number(r[k])));
    if (!allValid(readings)) return toast.error("All meter readings are required");
    if (isDual && !allValid(readings2)) return toast.error("MPD 2 readings are required");

    setBusy(true);
    try {
      const opening = Object.fromEntries(Object.entries(readings).map(([k, v]) => [k, Number(v)]));
      const body = { mpd, opening, opening_cash: Number(openingCash || 0) };
      if (isDual) body.opening_mpd2 = Object.fromEntries(Object.entries(readings2).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.post("/shifts/start", body);
      toast.success("Shift started");
      nav(`/shift/${data.id}/running`);
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  return (
    <StaffLayout>
      <div className="mb-4 flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav("/dashboard")} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-display font-bold">Start Shift</h1>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <Card className="p-5 rounded-2xl border-slate-200">
          <Label className="text-sm font-semibold">Select MPD</Label>
          <div className={`grid gap-3 mt-3 ${OPTIONS.length > 3 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-3"}`}>
            {OPTIONS.map((m) => (
              <label
                key={m}
                data-testid={`mpd-option-${m.replace(/\s+/g, "").toLowerCase()}`}
                onClick={() => setMpd(m)}
                className={`flex items-center justify-center gap-2 h-14 rounded-xl border-2 cursor-pointer transition-colors ${
                  mpd === m
                    ? "border-[color:var(--bpcl-blue)] bg-[color:var(--bpcl-blue)]/5 text-[color:var(--bpcl-blue)] font-semibold"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <span className="text-base">{m}</span>
              </label>
            ))}
          </div>
          {isDual && (
            <div className="mt-3 text-xs text-slate-500 rounded-lg bg-[color:var(--bpcl-blue)]/5 p-2.5 border border-[color:var(--bpcl-blue)]/20">
              You will manage both dispensers — enter openings for MPD 1 and MPD 2 separately. Two duty slips will print at end of shift.
            </div>
          )}
        </Card>

        <Card className="p-5 rounded-2xl border-slate-200">
          <Label className="text-sm font-semibold">Opening Cash (₹)</Label>
          <Input
            data-testid="opening-cash"
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            value={openingCash}
            onChange={(e) => setOpeningCash(e.target.value)}
            placeholder="0.00"
            className="mt-2 h-12 rounded-xl font-mono-num"
          />
        </Card>

        <div>
          <div className="text-sm font-semibold text-slate-700 mb-3">
            {isDual ? "MPD 1 · Opening Readings" : "Opening Readings"}
          </div>
          <ReadingsGrid
            values={readings}
            products={products}
            onChange={(k, v) => setReadings((r) => ({ ...r, [k]: v }))}
            prefix="opening"
          />
        </div>

        {isDual && (
          <div>
            <div className="text-sm font-semibold text-slate-700 mb-3">MPD 2 · Opening Readings</div>
            <ReadingsGrid
              values={readings2}
              products={products}
              onChange={(k, v) => setReadings2((r) => ({ ...r, [k]: v }))}
              prefix="opening2"
            />
          </div>
        )}

        <Button
          type="submit"
          disabled={busy}
          data-testid="start-fueling-btn"
          className="w-full h-14 rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)] text-base font-semibold"
        >
          <Play className="w-4 h-4 mr-2" />
          {busy ? "Starting…" : "START FUELING"}
        </Button>
      </form>
    </StaffLayout>
  );
}
